<template>
  <div class="font-roboto">
    <!-- Laptop and up: table (Figma "Dashboard - Grid View"). No inner scroll: a long day grows the card and the
         page scrolls, like the Work Order lists -->
    <!-- table from 1280: at 1024 the menu + calendar leave it ~370px and Status was cut off (QA D-52) -->
    <div class="hidden desktop:block overflow-x-auto customscrollbar">
      <table class="w-full border-separate border-spacing-y-1">
        <thead class="sticky top-0 z-[1]">
          <tr>
            <th v-for="(header, index) in headers" :key="header.key"
              class="px-2.5 py-2 !bg-[#BA90C6] font-roboto font-medium text-[12px] largeDesktop:text-[14px] !text-white whitespace-nowrap select-none"
              :class="{
                'rounded-l-[10px]': index === 0,
                'rounded-r-[10px]': index === headers.length - 1,
                'cursor-pointer': header.sortable !== false,
              }" @click="toggleSort(header)">
              <span class="flex items-center justify-between gap-2">
                {{ header.text }}
                <img v-if="header.sortable !== false" :src="sortIcon(header)" alt="Sort"
                  class="w-[12px] h-[12px] largeDesktop:w-4 largeDesktop:h-4 object-contain">
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, rowIndex) in pagedSortedItems" :key="rowIndex"
            class="cursor-pointer shadow-[0_0_5px_0_#00000026] hover:shadow-md transition-all overflow-hidden rounded-[10px]"
            @click="onRowClick(item, $event)">
            <td v-for="(header, colIndex) in headers" :key="header.key"
              class="px-2.5 py-2 bg-white font-roboto text-[12px] largeDesktop:text-[14px] font-normal text-[#333333]" :class="{
                'rounded-l-[10px]': colIndex === 0,
                'rounded-r-[10px]': colIndex === headers.length - 1,
                '!text-[#7695FF]': header.key === 'id',
              }">
              <!-- real <a href> when the row has a route, so it can be opened in a new tab -->
              <component :is="rowLink(item) ? 'router-link' : 'div'" :to="rowLink(item) || undefined"
                class="block no-underline text-inherit">
              <template v-if="header.key === 'textStatus'">
                <!-- same badge as the Service & Repair listing -->
                <span v-if="item.textStatus" class="text-white rounded-md px-2.5 py-1 capitalize block w-fit"
                  :style="{ backgroundColor: getStatusColor(item.textStatus) }">{{ formatStatus(item.textStatus) }}</span>
                <span v-else>-</span>
              </template>
              <!-- the partner's logo sits next to the ID (no separate Partners column) -->
              <template v-else-if="header.key === 'id'">
                <div class="flex items-center gap-2">
                  {{ $helper.isEmpty(item.id) ? '-' : item.id }}
                  <img v-if="item.image" :src="item.image" alt="Partner Company Logo"
                    class="w-10 h-7 object-contain flex-shrink-0">
                </div>
              </template>
              <template v-else>
                {{ $helper.isEmpty(item[header.key]) ? '-' : item[header.key] }}
              </template>
              </component>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Below laptop: cards (mobile design) -->
    <div class="flex flex-col desktop:hidden">
      <component
        :is="rowLink(item) ? 'router-link' : 'div'"
        v-for="(item, rowIndex) in pagedItems"
        :key="rowIndex"
        :to="rowLink(item) || undefined"
        class="block no-underline text-inherit px-2.5 py-1.5 my-0.5 rounded-[10px] border-1 cursor-pointer transition-all hover:shadow-md border-gray-300 hover:border-purple-300 bg-white"
        @click="onRowClick(item, $event)"
      >
        <div class="flex justify-between items-center w-full">
          <p class="m-0 text-[#9C9BBF] font-roboto text-base">
            {{ item.time }}
          </p>
          <img v-if="item.image" :src="item.image" alt="Partner Company Logo" class="w-12 h-12 object-contain max-h-10">
        </div>
        <div class="relative flex flex-col gap-1">
          <div>
            <span class="block text-[#EA8A8A] font-roboto text-[13px] leading-[18px]">ID:</span>
            <p class="m-0 font-roboto text-[14px] leading-[20px] font-400">
              {{ item.id }}
            </p>
          </div>
          <div>
            <span class="block text-[#EA8A8A] font-roboto text-[13px] leading-[18px]">Consumer Name:</span>
            <p class="m-0 font-roboto text-[14px] leading-[20px] font-400">
              {{ $helper.isEmpty(item.customerName) ? '-' : item.customerName }}
            </p>
          </div>
          <div>
            <span class="block text-[#EA8A8A] font-roboto text-[13px] leading-[18px]">Type:</span>
            <p class="m-0 font-roboto text-[14px] leading-[20px] font-400">
              {{ $helper.isEmpty(item.servicetype) ? '-' : item.servicetype }}
            </p>
          </div>
          <div>
            <span class="block text-[#EA8A8A] font-roboto text-[13px] leading-[18px]">Status:</span>
            <span v-if="item.textStatus" class="text-white rounded-md px-2 py-[2px] mt-[2px] capitalize block w-fit font-roboto text-[12px] leading-[16px]"
              :style="{ backgroundColor: getStatusColor(item.textStatus) }">{{ formatStatus(item.textStatus) }}</span>
            <p v-else class="m-0 font-roboto text-[14px] leading-[20px]">-</p>
          </div>
        </div>
      </component>
    </div>
  </div>
</template>

<script>
import sortingIcon from "@/assets/images/sorting-icon.png";
import ascIcon from "@/assets/images/asc.svg";
import descIcon from "@/assets/images/desc.svg";
import { rqStatusColor } from "@/services/rqApi";

export default {
  props: {
    // { key, text, sortable?: false, sortKey?: field to sort by instead of key }
    headers: {
      type: Array,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    // optional client-side paging (applied after sorting); perPage 0 = show all
    page: {
      type: Number,
      default: 1,
    },
    perPage: {
      type: Number,
      default: 0,
    },
    // optional item → route location (or null). Rows/cards with a location render as real links
    // (open in new tab, Ctrl/Cmd/middle click); others fall back to the row-clicked event
    rowTo: {
      type: Function,
      default: null,
    },
  },
  emits: ['row-clicked'],
  data() {
    return {
      sortKey: '',
      sortDirection: '',
    };
  },
  computed: {
    pagedSortedItems() {
      return this.pageSlice(this.sortedItems);
    },
    pagedItems() {
      return this.pageSlice(this.items);
    },
    sortedItems() {
      const header = this.headers.find(h => h.key === this.sortKey);
      if (!header || !this.sortDirection) return this.items;
      const field = header.sortKey || header.key;
      const dir = this.sortDirection === 'ASC' ? 1 : -1;
      return [...this.items].sort((a, b) => {
        const x = a[field] ?? '';
        const y = b[field] ?? '';
        if (typeof x === 'number' && typeof y === 'number') return (x - y) * dir;
        return String(x).localeCompare(String(y), undefined, { numeric: true }) * dir;
      });
    },
  },
  methods: {
    pageSlice(list) {
      if (!this.perPage) return list;
      const start = (this.page - 1) * this.perPage;
      return list.slice(start, start + this.perPage);
    },
    rowLink(item) {
      return this.rowTo ? this.rowTo(item) || null : null;
    },
    // clicks on the <a> itself are handled by router-link (incl. modifier/middle clicks);
    // clicks on the cell padding around it navigate the same way
    onRowClick(item, e) {
      if (e && e.target.closest && e.target.closest('a')) return;
      const to = this.rowLink(item);
      if (to) this.$router.push(to);
      else this.$emit('row-clicked', item);
    },
    toggleSort(header) {
      if (header.sortable === false) return;
      if (this.sortKey !== header.key) {
        this.sortKey = header.key;
        this.sortDirection = 'ASC';
      } else {
        this.sortDirection = this.sortDirection === 'ASC' ? 'DESC' : (this.sortDirection === 'DESC' ? '' : 'ASC');
      }
    },
    sortIcon(header) {
      if (this.sortKey !== header.key || !this.sortDirection) return sortingIcon;
      return this.sortDirection === 'ASC' ? ascIcon : descIcon;
    },
    // API sends some statuses upper-case ("PENDING"); Figma shows title case
    formatStatus(status) {
      return status ? status.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) : '';
    },
    // Service & Repair listing colours (serviceRepairTable.vue); other job types fall back to the RQ colours
    getStatusColor(status) {
      const sarColors = {
        'EN ROUTE': '#65B741',
        'PENDING': '#F39C12',
        'CANCELLED': '#D7385E',
        'NO SALE': '#CA6180',
        'NEW SYSTEM': '#1D5D9B',
        'PAYMENT PENDING': '#F59E0B',
        'ARRIVED': '#635985',
        'CONVERTED TO BOOKING': '#635985',
        'CLOSED': '#686D76',
        'COMPLETED': '#65B741',
        'EVALUATION STARTED': '#5E366A',
        'STARTED': '#03AED2',
        'SUSPENDED': '#B91C1C',
        'EVALUATION COMPLETED': '#65B741',
        'ACCEPTED': '#7695FF',
        'CONSUMER QUOTE DECLINE': '#D7385E',
      };
      return sarColors[String(status).toUpperCase()] || rqStatusColor(status);
    }
  }
};
</script>
<style>
.customscrollbar::-webkit-scrollbar {
    width: 5px !important;
    height: 5px !important;
}

.customscrollbar::-webkit-scrollbar-track {
    box-shadow: inset 0 0 5px #E3E3E3;
    border-radius: 10px;
}

.customscrollbar::-webkit-scrollbar-thumb {
    background: #D4D4D4;
    border-radius: 10px;
}
</style>
